import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Trash2, MessageSquare, UserX, AlertTriangle, CheckCircle, XCircle, Loader2, Brain, Info } from 'lucide-react';
import DeleteMemoryDialog from './DeleteMemoryDialog';
import DeleteAccountDialog from './DeleteAccountDialog';
import TerminalOutput from './TerminalOutput';

const UserSettings = () => {
  const { user, logout } = useAuth();
  const [showDeleteMemoryDialog, setShowDeleteMemoryDialog] = useState(false);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [terminalOutput, setTerminalOutput] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Memory preferences state
  const [mem0Enabled, setMem0Enabled] = useState(true);
  const [mem0Available, setMem0Available] = useState(true);
  const [deleteExistingMemories, setDeleteExistingMemories] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [preferenceMessage, setPreferenceMessage] = useState(null);

  // Load memory preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/preferences`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMem0Enabled(response.data.mem0_enabled);
        setLoadingPreferences(false);
      } catch (error) {
        console.error('Error loading preferences:', error);
        setLoadingPreferences(false);
      }
    };
    loadPreferences();
  }, []);

  // Save memory preferences
  const handleSaveMemoryPreferences = async () => {
    setSavingPreferences(true);
    setPreferenceMessage(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/preferences/memory`,
        {
          mem0_enabled: mem0Enabled,
          delete_existing_memories: deleteExistingMemories
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMem0Available(response.data.mem0_available);
      setPreferenceMessage({
        type: 'success',
        text: response.data.message || 'Memory preferences updated successfully'
      });
      
      // Reset the delete checkbox after saving
      setDeleteExistingMemories(false);
      
      // Clear message after 3 seconds
      setTimeout(() => setPreferenceMessage(null), 3000);
      
    } catch (error) {
      console.error('Error saving preferences:', error);
      setPreferenceMessage({
        type: 'error',
        text: 'Failed to save preferences. Please try again.'
      });
    } finally {
      setSavingPreferences(false);
    }
  };

  const handleDeleteMemory = () => {
    setShowDeleteMemoryDialog(true);
  };

  const handleDeleteAccount = () => {
    setShowDeleteAccountDialog(true);
  };

  const handleDeleteMemoryConfirm = async () => {
    setIsProcessing(true);
    setShowDeleteMemoryDialog(false);
    
    const terminal = {
      title: "Deleting Conversation Memory",
      operations: [
        { name: "Connecting to server...", status: "running" },
        { name: "Deleting conversation sessions", status: "pending" },
        { name: "Deleting conversation messages", status: "pending" },
        { name: "Deleting Mem0 memories", status: "pending" },
        { name: "Cleaning up pseudonym mappings", status: "pending" },
        { name: "Deleting usage logs", status: "pending" }
      ]
    };
    
    setTerminalOutput(terminal);

    try {
      // Update first operation
      updateTerminalOperation(0, "completed");
      updateTerminalOperation(1, "running");

      // Call the API
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/chat/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update operations based on response
      updateTerminalOperation(1, "completed");
      updateTerminalOperation(2, "running");
      await new Promise(resolve => setTimeout(resolve, 300));
      
      updateTerminalOperation(2, "completed");
      updateTerminalOperation(3, "running");
      await new Promise(resolve => setTimeout(resolve, 500));
      
      updateTerminalOperation(3, "completed");
      updateTerminalOperation(4, "running");
      await new Promise(resolve => setTimeout(resolve, 200));
      
      updateTerminalOperation(4, "completed");
      updateTerminalOperation(5, "running");
      await new Promise(resolve => setTimeout(resolve, 200));
      
      updateTerminalOperation(5, "completed");

      // Add success message
      setTimeout(() => {
        setTerminalOutput(prev => ({
          ...prev,
          completed: true,
          success: true,
          message: "✅ All conversation memory has been successfully deleted!"
        }));
      }, 500);

    } catch (error) {
      console.error('Error deleting memory:', error);
      setTerminalOutput(prev => ({
        ...prev,
        completed: true,
        success: false,
        message: "❌ Failed to delete conversation memory. Please try again."
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteAccountConfirm = async (password) => {
    setIsProcessing(true);
    setShowDeleteAccountDialog(false);
    
    const terminal = {
      title: "Deleting User Account",
      operations: [
        { name: "Verifying password...", status: "running" },
        { name: "Deleting user profile", status: "pending" },
        { name: "Deleting children profiles", status: "pending" },
        { name: "Deleting activities", status: "pending" },
        { name: "Deleting conversation history", status: "pending" },
        { name: "Deleting Mem0 memories", status: "pending" },
        { name: "Deleting LLM usage records", status: "pending" },
        { name: "Deleting expert ratings", status: "pending" },
        { name: "Cleaning up audit logs", status: "pending" }
      ]
    };
    
    setTerminalOutput(terminal);

    try {
      // Update first operation
      updateTerminalOperation(0, "running");

      // Call the API
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { password }
      });

      // Update operations based on response
      updateTerminalOperation(0, "completed");
      updateTerminalOperation(1, "running");
      await new Promise(resolve => setTimeout(resolve, 300));
      
      updateTerminalOperation(1, "completed");
      updateTerminalOperation(2, "running");
      await new Promise(resolve => setTimeout(resolve, 400));
      
      updateTerminalOperation(2, "completed");
      updateTerminalOperation(3, "running");
      await new Promise(resolve => setTimeout(resolve, 300));
      
      updateTerminalOperation(3, "completed");
      updateTerminalOperation(4, "running");
      await new Promise(resolve => setTimeout(resolve, 400));
      
      updateTerminalOperation(4, "completed");
      updateTerminalOperation(5, "running");
      await new Promise(resolve => setTimeout(resolve, 300));
      
      updateTerminalOperation(5, "completed");
      updateTerminalOperation(6, "running");
      await new Promise(resolve => setTimeout(resolve, 200));
      
      updateTerminalOperation(6, "completed");
      updateTerminalOperation(7, "running");
      await new Promise(resolve => setTimeout(resolve, 200));
      
      updateTerminalOperation(7, "completed");
      updateTerminalOperation(8, "running");
      await new Promise(resolve => setTimeout(resolve, 200));
      
      updateTerminalOperation(8, "completed");

      // Add success message and logout
      setTimeout(() => {
        setTerminalOutput(prev => ({
          ...prev,
          completed: true,
          success: true,
          message: "✅ Account has been successfully deleted. You will be logged out automatically."
        }));
        
        // Logout after a delay
        setTimeout(() => {
          logout();
        }, 2000);
      }, 500);

    } catch (error) {
      console.error('Error deleting account:', error);
      setTerminalOutput(prev => ({
        ...prev,
        completed: true,
        success: false,
        message: "❌ Failed to delete account. Please check your password and try again."
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  const updateTerminalOperation = (index, status) => {
    setTerminalOutput(prev => ({
      ...prev,
      operations: prev.operations.map((op, i) => 
        i === index ? { ...op, status } : op
      )
    }));
  };

  const closeTerminal = () => {
    setTerminalOutput(null);
  };

  const getRoleBadge = (role) => {
    const variants = {
      user: "default",
      admin: "secondary", 
      expert: "outline",
      superadmin: "destructive"
    };
    
    return (
      <Badge variant={variants[role] || "default"} className="ml-2">
        {role?.toUpperCase() || 'USER'}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy & Account Settings</h1>
        <p className="text-gray-600">Manage your memory preferences, conversation data, and account</p>
      </div>

      {/* User Info Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <UserX className="h-5 w-5 mr-2" />
            Account Information
          </CardTitle>
          <CardDescription>
            Your current account details and role
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Email:</span>
              <span className="text-sm text-gray-900">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Full Name:</span>
              <span className="text-sm text-gray-900">{user?.full_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Role:</span>
              {getRoleBadge(user?.role)}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Account Created:</span>
              <span className="text-sm text-gray-900">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Memory Preferences Card */}
      <Card className="mb-6 border-emerald-200">
        <CardHeader>
          <CardTitle className="flex items-center text-emerald-700">
            <Brain className="h-5 w-5 mr-2" />
            Smart Memory Preferences
          </CardTitle>
          <CardDescription>
            Control how the AI remembers information across conversations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingPreferences ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
              <span className="ml-2 text-sm text-gray-600">Loading preferences...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Mem0 Toggle */}
              <div className="flex items-start justify-between py-2">
                <div className="flex-1 pr-4">
                  <div className="flex items-center mb-1">
                    <label className="text-sm font-medium text-gray-900">
                      Cross-Session Memory
                    </label>
                    {!mem0Available && (
                      <Badge variant="outline" className="ml-2 text-xs">
                        Unavailable
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    Remember important details (child's age, preferences, past topics) across different conversations
                  </p>
                </div>
                <Switch
                  checked={mem0Enabled}
                  onCheckedChange={setMem0Enabled}
                  disabled={loadingPreferences}
                />
              </div>

              {/* Delete Existing Memories Option (only when disabling Mem0) */}
              {!mem0Enabled && (
                <Alert className="border-amber-200 bg-amber-50/50">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <p className="text-sm text-amber-800">
                        Disabling cross-session memory will only affect future conversations.
                      </p>
                      <label className="flex items-start space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={deleteExistingMemories}
                          onChange={(e) => setDeleteExistingMemories(e.target.checked)}
                          className="mt-0.5 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-amber-800">
                          <strong>Also delete my existing memories</strong>
                          <br />
                          <span className="text-xs text-amber-700">
                            Warning: This cannot be undone
                          </span>
                        </span>
                      </label>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Info about Mem0 */}
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  {mem0Enabled ? (
                    <>
                      <strong>Enabled:</strong> The AI will remember important information across all your conversations.
                      This helps provide more personalized and context-aware responses.
                    </>
                  ) : (
                    <>
                      <strong>Disabled:</strong> The AI will only remember what you tell it within each conversation session.
                      Each new conversation starts fresh.
                    </>
                  )}
                </AlertDescription>
              </Alert>

              {/* Mem0 Unavailable Warning */}
              {!mem0Available && (
                <Alert className="border-blue-200 bg-blue-50">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-sm text-blue-800">
                    Smart Memory is currently unavailable. Your preferences will take effect when it's enabled by administrators.
                  </AlertDescription>
                </Alert>
              )}

              {/* Success/Error Message */}
              {preferenceMessage && (
                <Alert className={preferenceMessage.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}>
                  {preferenceMessage.type === 'success' ? (
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600" />
                  )}
                  <AlertDescription className={preferenceMessage.type === 'success' ? 'text-emerald-800' : 'text-red-800'}>
                    {preferenceMessage.text}
                  </AlertDescription>
                </Alert>
              )}

              {/* Save Button */}
              <Button
                onClick={handleSaveMemoryPreferences}
                disabled={savingPreferences || loadingPreferences}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {savingPreferences ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Save Memory Preferences
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Management Section */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Delete Memory Card */}
        <Card className="border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center text-orange-700">
              <MessageSquare className="h-5 w-5 mr-2" />
              Delete Conversation Memory
            </CardTitle>
            <CardDescription>
              Remove all your conversation history and AI memory
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This will permanently delete all your conversation history, including:
                <ul className="mt-2 ml-4 list-disc text-sm">
                  <li>All conversation sessions</li>
                  <li>All chat messages</li>
                  <li>AI memory of your conversations</li>
                  <li>Pseudonym mappings</li>
                </ul>
              </AlertDescription>
            </Alert>
            <Button 
              onClick={handleDeleteMemory}
              disabled={isProcessing}
              variant="outline"
              className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Memory
            </Button>
          </CardContent>
        </Card>

        {/* Delete Account Card */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center text-red-700">
              <UserX className="h-5 w-5 mr-2" />
              Delete Account
            </CardTitle>
            <CardDescription>
              Permanently delete your account and all associated data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This will permanently delete your entire account, including:
                <ul className="mt-2 ml-4 list-disc text-sm">
                  <li>Your user profile</li>
                  <li>All children profiles</li>
                  <li>All activities and milestones</li>
                  <li>All conversation history</li>
                  <li>All AI memories</li>
                  <li>All usage records</li>
                </ul>
              </AlertDescription>
            </Alert>
            <Button 
              onClick={handleDeleteAccount}
              disabled={isProcessing}
              variant="outline"
              className="w-full border-red-300 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Account
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <DeleteMemoryDialog
        isOpen={showDeleteMemoryDialog}
        onClose={() => setShowDeleteMemoryDialog(false)}
        onConfirm={handleDeleteMemoryConfirm}
      />

      <DeleteAccountDialog
        isOpen={showDeleteAccountDialog}
        onClose={() => setShowDeleteAccountDialog(false)}
        onConfirm={handleDeleteAccountConfirm}
      />

      {/* Terminal Output */}
      {terminalOutput && (
        <TerminalOutput
          output={terminalOutput}
          onClose={closeTerminal}
        />
      )}
    </div>
  );
};

export default UserSettings;
